import React, { useState, useEffect, useCallback } from 'react';
import { FiArrowLeft, FiCheck, FiX, FiSkipForward, FiAlertTriangle, FiMessageSquare, FiSend } from 'react-icons/fi';
import { fetchWorkflow, updateWorkflowStep, createComment } from '../../utils/sentimentApi';

const STEP_STATUS_LABELS = { completed: 'Completed', in_progress: 'In Progress', pending: 'Pending', failed: 'Failed', skipped: 'Skipped' };
const PRIORITY_COLORS    = { P1: 'sm-badge-p1', P2: 'sm-badge-p2', P3: 'sm-badge-p3' };

export default function SentimentWorkflow({ alertId, onBack, showToast }) {
  const [workflow, setWorkflow]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [updating, setUpdating]   = useState(null);

  const [stepModal, setStepModal] = useState(null);
  const [actor, setActor]         = useState('');
  const [notes, setNotes]         = useState('');

  const [newComment, setNewComment]     = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  const userEmail = localStorage.getItem('sentiment_user_email') || '';

  const load = useCallback(async () => {
    if (!alertId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchWorkflow(alertId);
      setWorkflow(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => { load(); }, [load]);

  const handleStepAction = async (status) => {
    if (!stepModal) return;
    setUpdating(stepModal.step_id);
    try {
      await updateWorkflowStep(stepModal.step_id, { status, actor: actor.trim() || undefined, notes: notes.trim() || undefined });
      setWorkflow(prev => ({
        ...prev,
        steps: prev.steps.map(s =>
          s.step_id === stepModal.step_id
            ? { ...s, status, actor: actor.trim() || s.actor, notes: notes.trim() || s.notes, completed_at: status === 'completed' ? new Date().toISOString() : s.completed_at }
            : s
        ),
      }));
      setStepModal(null);
      setActor('');
      setNotes('');
      showToast?.(`Step marked ${status}`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setUpdating(null);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setCommentLoading(true);
    try {
      const comment = await createComment({
        customer_id: workflow?.customer_id,
        alert_id: alertId,
        content: newComment.trim(),
      });
      setWorkflow(prev => ({
        ...prev,
        comments: [...(prev.comments || []), comment],
      }));
      setNewComment('');
      showToast?.('Comment added', 'success');
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setCommentLoading(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;

  if (!alertId) {
    return (
      <div className="sm-empty">
        <span className="sm-empty-icon">🔍</span>
        <span>No alert selected. Go to <strong>Alerts</strong> and click <strong>Workflow</strong> on an alert.</span>
        <button className="sm-btn sm-btn-outline" onClick={onBack}><FiArrowLeft size={14} /> Back to Alerts</button>
      </div>
    );
  }

  if (loading) return <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading workflow…</span></div>;

  return (
    <div>
      <div className="sm-section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="sm-btn sm-btn-ghost" onClick={onBack}><FiArrowLeft size={14} /> Alerts</button>
          <h2 className="sm-section-title" style={{ margin: 0 }}>Workflow</h2>
          {workflow?.priority && (
            <span className={`sm-badge ${PRIORITY_COLORS[workflow.priority] || ''}`}>{workflow.priority}</span>
          )}
        </div>
        {workflow?.customer_id && (
          <span style={{ fontSize: 13, color: 'var(--sm-muted)' }}>Customer: <strong>{workflow.customer_id}</strong></span>
        )}
      </div>

      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {workflow && (
        <>
          <div className="sm-card">
            <div className="sm-steps">
              {(workflow.steps || []).sort((a, b) => a.step_order - b.step_order).map((step, idx, arr) => (
                <div className="sm-step" key={step.step_id} style={{ '--step-delay': `${idx * 120}ms` }}>
                  <div className="sm-step-line">
                    <div className={`sm-step-dot ${step.status}`}>
                      {step.status === 'completed' ? <FiCheck size={13} />
                        : step.status === 'failed'  ? <FiX size={13} />
                        : step.status === 'skipped' ? <FiSkipForward size={13} />
                        : step.step_order}
                    </div>
                    {idx < arr.length - 1 && <div className="sm-step-connector" />}
                  </div>
                  <div className="sm-step-body">
                    <div className="sm-step-name">{step.step_name}</div>
                    <div className="sm-step-meta">
                      <span className={`sm-badge sm-badge-${step.status === 'completed' ? 'resolved' : step.status === 'in_progress' ? 'acknowledged' : step.status === 'failed' ? 'critical' : 'pending'}`}>
                        {STEP_STATUS_LABELS[step.status] || step.status}
                      </span>
                      {step.actor && <span style={{ marginLeft: 8 }}>by <strong>{step.actor}</strong></span>}
                      {step.completed_at && <span style={{ marginLeft: 8, color: 'var(--sm-muted)' }}>{fmtDate(step.completed_at)}</span>}
                    </div>
                    {step.notes && (
                      <div className="sm-step-notes">{step.notes}</div>
                    )}
                    {(step.status === 'pending' || step.status === 'in_progress') && (
                      <div className="sm-step-actions">
                        <button
                          className="sm-btn sm-btn-primary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setStepModal(step)}
                          disabled={!!updating}
                        >
                          <FiCheck size={12} /> Update
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div className="sm-card" style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <FiMessageSquare size={15} color="var(--sm-primary)" />
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Comments {workflow.comments?.length > 0 && `(${workflow.comments.length})`}
              </span>
            </div>

            {(workflow.comments || []).length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: '0 0 14px' }}>No comments yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                {workflow.comments.map(c => (
                  <div key={c.comment_id} style={{ background: 'var(--sm-bg)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{c.user_name || c.customer_id}</span>
                      <span style={{ fontSize: 11, color: 'var(--sm-muted)' }}>{fmtDate(c.created_at)}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--sm-ink)' }}>{c.content}</p>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="sm-input-search"
                style={{ flex: 1 }}
                placeholder="Add a comment…"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
              />
              <button
                className="sm-btn sm-btn-primary"
                style={{ padding: '6px 14px', fontSize: 13 }}
                onClick={handleAddComment}
                disabled={commentLoading || !newComment.trim()}
              >
                <FiSend size={13} /> {commentLoading ? '…' : 'Send'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Step update modal */}
      {stepModal && (
        <div className="sm-modal-overlay" onClick={() => setStepModal(null)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sm-modal-title">Update Step</h3>
            <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: 0 }}>{stepModal.step_name}</p>
            <div className="sm-field">
              <label>Actor (your name)</label>
              <input type="text" value={actor} onChange={e => setActor(e.target.value)} placeholder={userEmail || 'e.g. R. Kumar'} autoFocus />
            </div>
            <div className="sm-field">
              <label>Notes (optional)</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="What was done?" />
            </div>
            <div className="sm-modal-footer" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="sm-btn sm-btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => handleStepAction('skipped')} disabled={!!updating}>
                  <FiSkipForward size={12} /> Skip
                </button>
                <button className="sm-btn sm-btn-danger" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => handleStepAction('failed')} disabled={!!updating}>
                  <FiX size={12} /> Failed
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="sm-btn sm-btn-ghost" onClick={() => setStepModal(null)}>Cancel</button>
                <button className="sm-btn sm-btn-primary" onClick={() => handleStepAction('completed')} disabled={!!updating}>
                  <FiCheck size={12} /> {updating ? 'Saving…' : 'Mark Complete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
